'use client';

import { variables } from './variables';
import { Interpolation, Theme, createTheme } from '@mui/material/styles';
import { addOpacityToThemeColor } from 'utils/styles/addOpacity';
import { createBreakpoints } from '@mui/system';
import sharedTheme from '../shared/theme';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { SuccessIcon } from '@/assets/theme/pharmainsight/alertIcons/SuccessIcon';
import { WarningIcon } from '@/assets/theme/pharmainsight/alertIcons/WarningIcon';
import { ErrorIcon } from '@/assets/theme/pharmainsight/alertIcons/ErrorIcon';
import { InfoIcon } from '@/assets/theme/pharmainsight/alertIcons/InfoIcon';

const { colors, fonts, borderRadius, padding, boxShadows, typography } = variables;

const breakpoints = createBreakpoints({});

const headerButtonStyles: Interpolation<{ theme: Theme }> = {
    color: colors.other.text.header,
    backgroundColor: 'none',
    fontFamily: fonts.secondary,
    fontSize: '1rem',
    lineHeight: '1rem',
    fontWeight: '400',
    padding: '8px 16px',
    margin: '0 4px',
    height: '38px',
    borderRadius: borderRadius.topMenu,
    textTransform: 'initial',
    '&:hover': {
        color: colors.palette.primary['200'],
    },
    '&.active, &.active:hover': {
        color: colors.palette.primary['300'],
        borderRadius: borderRadius.topMenu,
        backgroundColor: colors.palette.background.menuItemHover,
    },
    '& .MuiTouchRipple-root': {
        display: 'none',
    },
};

const defaultTheme = sharedTheme(variables);

const outlinedInputStyles = {
    styleOverrides: {
        root: {
            ...defaultTheme.MuiOutlinedInput.styleOverrides.root,
            borderRadius: borderRadius.inputs,
            fontFamily: fonts.secondary,

            '.MuiOutlinedInput-notchedOutline, .MuiPickersOutlinedInput-notchedOutline': {
                transition: 'all 0.15s ease-in-out',
            },

            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: colors.other.inputs.focus,
                    boxShadow: boxShadows.inputShadow,
                },
            },

            '&.Mui-disabled': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline':
                    {
                        borderColor: 'transparent',
                    },
            },
        },

        notchedOutline: {
            borderRadius: borderRadius.inputs,
        },
    },
};

export const theme = createTheme({
    colors,
    fonts,
    borderRadius,
    padding,
    palette: colors.palette,
    typography,
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    lineHeight: '140%',
                    '&.homePageTitle': {
                        width: '100%',
                        color: colors.palette.additional['500'],
                        fontSize: '1.5rem',
                        fontWeight: '600',
                    },
                    '&.homePageDescription': {
                        fontSize: '1rem',
                        fontWeight: '400',
                        marginBottom: '24px',
                        order: '2',
                    },
                    '&.badgeDefinitionTitle, &.introTitle': {
                        color: colors.palette.neutral['800'],
                        fontFamily: fonts.primary,
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        margin: '8px auto',
                    },
                    '&.badgeDefinitionDescription, &.introDescription': {
                        fontFamily: fonts.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '400',
                        color: colors.palette.text.secondary,
                        textTransform: 'none',
                    },
                    '&.nmDashboardWelcomeTitle': {
                        color: colors.palette.primary.main,
                        textTransform: 'lowercase',
                        '&:first-letter': {
                            textTransform: 'uppercase',
                        },
                    },
                    '&.nmDashboardJoinTitle': {
                        color: colors.palette.primary.main,
                    },
                    '&.nmDashboardNote': {
                        fontSize: '0.75rem',
                    },
                    '&.surveyCardProjectNumber': {
                        fontSize: '0.875rem',
                        margin: '0',
                        color: colors.palette.neutral['700'],
                    },
                    '&.surveyCardTitle': {
                        fontSize: '1.125rem',
                        margin: '0',
                    },
                    '&.surveyDetailsTitle': {
                        fontSize: '0.875rem',
                        color: colors.palette.neutral['400'],
                    },
                    '&.surveyDetailsValue': {
                        color: colors.palette.neutral['700'],
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        marginRight: '16px',
                        fontWeight: '600',
                    },
                    '&.dynamicStaticPagesLink': {
                        color: colors.palette.primary.main,
                        fontWeight: '600',
                        textDecoration: 'underline',
                    },
                    '&.ProfileQuestionTitle': {
                        fontSize: '1rem',
                        color: colors.palette.neutral['700'],
                    },
                    '&.referralCopyLink': {
                        color: colors.palette.tertiary['500'],
                    },
                    [breakpoints.up('lg')]: {
                        '&.homePageTitle, &.homePageDescription': {
                            paddingRight: '160px',
                        },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                ...defaultTheme.MuiCssBaseline.styleOverrides,
                'strong, b': {
                    fontWeight: '600',
                },
                '&.language-info .MuiBox-root, &.language-button .MuiBox-root': {
                    fontWeight: '400',
                    fontSize: '1rem',
                },
                '&.language-info .MuiBox-root': {
                    color: colors.palette.primary['400'],
                },
                '&.introRegistrationWrapper': {
                    position: 'relative',
                    background:
                        'url("/images/themes/pharmainsight/registration-intro-bg.jpg") center/cover no-repeat',
                },
                '&.introRegistrationWrapper::before': {
                    display: 'block',
                    position: 'absolute',
                    content: '""',
                    width: '100%',
                    top: '0',
                    left: '0',
                    bottom: '0',
                    opacity: '0.9',
                    background: colors.palette.primary['500'],
                    backdropFilter: 'blur(6px)',
                    zIndex: '1',
                },
                '&.introRegistrationWrapper .MuiContainer-root': {
                    position: 'relative',
                    zIndex: '2',
                },
                '.topMenuLinksList': {
                    position: 'relative',
                    left: '0',
                    transform: 'translateX(0)',
                    a: {
                        textDecoration: 'none',
                    },
                },
                '&.triangleArrowUp': {
                    borderBottom: `5px solid ${colors.other.text.header}`,
                },
                '&.triangleArrowDown': {
                    borderTop: `5px solid ${colors.other.text.header}`,
                },
                '&.introIcons': {
                    backgroundColor: 'transparent',
                },
                '&.MuiPaper-root.infoBox': {
                    border: 'none',
                    backgroundColor: 'transparent',
                    paddingTop: '32px',
                },
                '&.introStack': {
                    justifyContent: 'space-between',
                    marginTop: '-12px',
                },
                '&.introStack .homeIntroIconsItem': {
                    flexBasis: '100%',
                    maxWidth: '100%',
                    border: `1px solid ${colors.palette.border.main}`,
                    background: 'white',
                    borderRadius: borderRadius.primary,
                    margin: '12px 0 0',
                },
                '&.homeIntroIconsItem .introIcon': {
                    width: '40px',
                    height: '40px',
                },
                '&.verificationFormElementBlock ': {
                    paddingRight: '8px',
                    paddingLeft: '8px',
                },
                '&.verificationSubmitButtonWrapper': {
                    paddingRight: '8px',
                },
                '&.paymentDescription, &.paymentDate': {
                    color: colors.palette.neutral['300'],
                },
                '&.paymentMethodTitle': {
                    color: colors.palette.neutral['700'],
                    marginBottom: '8px',
                },
                '&.paymentMethodComingSoonLabel': {
                    color: colors.other.text.info,
                    background: 'rgb(229, 246, 253)',
                    padding: '2px 6px',
                    top: '0',
                    right: '0',
                },
                '.referralInfoWrapper': {
                    paddingLeft: '8px',
                    paddingTop: '8px',
                },
                '.referralActionButtonBlock': {
                    padding: '16px 24px 24px 24px',
                },
                '.referralDetailsId': {
                    backgroundColor: 'rgba(252,252,252,1)',
                    boxShadow: '0 1px 0px rgba(232,232,232,1)',
                },
                '.referralsDetailTitle': {
                    color: colors.palette.primary.main,
                },
                '.referralsFiltersWrapper': {
                    padding: '24px',
                },
                '.privacyterms': {
                    fontSize: '17px',
                    fontWeight: '600',
                    paddingBottom: '10px',
                },
                '&.WalletMinMaxLabel': {
                    backgroundColor: addOpacityToThemeColor(colors.palette.primary['300'], 0.1),
                    color: colors.palette.primary['300'],
                    borderRadius: borderRadius.primary,
                },
                '.AccountUserIdBlock ': {
                    backgroundColor: colors.other.background.footer,
                    borderRadius: borderRadius.primary,
                },
                '.AccountUserId': {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors.palette.text.primary,
                },
                '&.ForgotPasswordLink': {
                    fontSize: '0.875rem',
                    fontWeight: '500',
                },
                '&.introTextBlock': {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: '100%',
                    '.ExternalBannerLink': {
                        borderRadius: borderRadius.primary,
                        border: `1px solid ${colors.palette.tertiary['500']}`,
                        marginTop: 'auto',
                        marginBottom: '0',
                        padding: '0',
                        order: '3',
                    },
                    '.ExternalBannerLink:hover': {
                        backgroundColor: colors.palette.tertiary['500'],
                        borderColor: colors.palette.tertiary['500'],
                    },
                    '.ExternalBannerLink:active': {
                        backgroundColor: colors.palette.tertiary['700'],
                        borderColor: colors.palette.tertiary['700'],
                    },
                    '.ExternalBannerLink a': {
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        fontWeight: '400',
                        color: 'white',
                        padding: '10px 16px',
                        transition: 'all 0.15s ease-in-out',
                    },
                    '.ExternalBannerLink a:hover': {
                        textDecoration: 'none',
                    },
                    '.ExternalBannerLink .MuiStack-root': {
                        width: '100%',
                        justifyContent: 'space-between',
                    },
                },
                '&.Header': {
                    borderBottom: `1px solid ${colors.palette.border.main}`,
                    '.headerWrapper': {
                        paddingTop: '10px',
                        paddingBottom: '10px',
                    },
                    '.HeaderPanelImageLogo': {
                        width: '126px',
                        height: '46px',
                    },
                    '.HeaderLogo, .HeaderLogo a': {
                        width: '100%',
                        height: '100%',
                    },
                    '.HeaderLogo a': {
                        display: 'inline-flex',
                        background:
                            'url("/images/themes/pharmainsight/logo-header-mobile.svg") no-repeat 0 0',
                    },
                    '.HeaderLogo img': {
                        width: '100%',
                        height: 'auto',
                        display: 'none',
                    },
                },
                '&.Footer': {
                    borderTop: `1px solid ${colors.palette.primary[50]}`,
                    '.MuiStack-root': {
                        alignItems: 'flex-start',
                    },
                    '.FooterLogo': {
                        paddingTop: 0,
                        paddingBottom: '16px',
                        'a img': {
                            width: '208px',
                            height: '51px',
                        },
                    },
                    '.footerMenu': {
                        flexDirection: 'column',
                        li: {
                            textAlign: 'left',
                            marginBottom: '8px',
                        },
                    },
                    '.copyright': {
                        textAlign: 'left',
                    },
                },
                [breakpoints.up('sm')]: {
                    '&.Footer': {
                        '.MuiStack-root': {
                            alignItems: 'flex-start',
                        },
                        '.FooterLogo': {
                            paddingBottom: 0,
                            'a img': {
                                width: '155px',
                                height: '38px',
                            },
                        },
                        '.footerMenu': {
                            flexDirection: 'row',
                        },
                    },
                },
                [breakpoints.up('md')]: {
                    '&.introStack': {
                        marginTop: '-24px',
                    },
                    '&.introStack .homeIntroIconsItem': {
                        flexBasis: 'calc(33.3% - 15px)',
                        maxWidth: 'calc(33.3% - 15px)',
                        marginTop: '24px',
                    },
                    '&.Header': {
                        '.HeaderPanelImageLogo': {
                            width: '192px',
                            height: '48px',
                        },
                        '.HeaderLogo a': {
                            background: 'none',
                        },
                        '.HeaderLogo img': {
                            display: 'block',
                        },
                    },
                },
                [breakpoints.up('lg')]: {
                    '&.introTextBlock .ExternalBannerLink': {
                        marginRight: '160px',
                    },
                    '&.introRegistrationWrapper::before': {
                        width: 'calc(50% - 80px)',
                    },
                    '&.introRegistrationWrapper .introTextBlock': {
                        padding: '24px 0',
                        width: '50%',
                        marginRight: '0',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiPaper.styleOverrides.root,
                    border: `1px solid ${colors.palette.border.main}`,
                    boxShadow: 'none',
                    '&.lang-switcher-wrapper': {
                        maxHeight: 'calc(100% - 32px)',
                    },
                    '&.warningBox': {
                        border: `1px solid ${colors.other.text.warning}`,
                        h3: {
                            color: colors.palette.primary['500'],
                            fontSize: '1.125rem',
                            paddingTop: '3px',
                        },
                    },
                    '&.introPicBlock': {
                        overflow: 'hidden',
                        borderRadius: borderRadius.primary,
                        padding: 0,
                        lineHeight: 0,
                        border: '0',
                        background: 'transparent',
                        boxShadow: 'none',
                        marginTop: '24px',
                        maxHeight: '340px',
                        order: '1',
                        img: {
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                        },
                    },
                    '&.accountTaxDetails': {
                        '.helperText': {
                            position: 'relative',
                            backgroundColor: colors.palette.background.default,
                            padding: '16px',
                            [breakpoints.up('md')]: {
                                marginLeft: '20px',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '15px',
                                    left: '-9px',
                                    width: '0',
                                    height: '0',
                                    borderTop: '10px solid transparent',
                                    borderBottom: '10px solid transparent',
                                    borderRight: `10px solid ${colors.palette.background.default}`,
                                    transform: 'translateY(-50%)',
                                },
                            },
                        },
                    },
                    '&.infoCard': {
                        background: 'transparent',
                        border: 'none',
                        marginTop: '12px',
                        '.infoCardRow': {
                            justifyContent: 'space-between',
                            flexWrap: 'nowrap',
                            columnGap: '24px',
                        },
                        '.infoCardItem': {
                            flexBasis: '100%',
                            maxWidth: '100%',
                            marginTop: '12px',
                            border: `1px solid ${colors.palette.border.main}`,
                            background: 'white',
                            borderRadius: borderRadius.primary,
                        },
                        [breakpoints.up('sm')]: {
                            '.infoCardItem': {
                                flexBasis: 'calc(50% - 6px)',
                                maxWidth: 'calc(50% - 6px)',
                            },
                        },
                        [breakpoints.up('lg')]: {
                            '.infoCardRow': {
                                marginTop: '24px',
                            },
                            '.infoCardItem': {
                                marginTop: 0,
                                flexBasis: 'calc(33.3% - 15px)',
                                maxWidth: 'calc(33.3% - 15px)',
                            },
                        },
                    },
                    '&.accountMenuWrapper': {
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                    },
                    '&.dynamicStaticPagesItem': {
                        transition: 'all 0.15s',
                    },
                    '&.dynamicStaticPagesItem:hover': {
                        borderColor: colors.palette.primary.main,
                    },
                    '.ProfileQuestionsContent': {
                        border: '1px solid transparent',
                    },
                    '&.WalletUploadFileInput': {
                        ...defaultTheme.MuiPaper.styleOverrides.root['&.WalletUploadFileInput'],
                        borderRadius: borderRadius.buttons,
                    },
                    '&.MuiAccordion-rounded, &.MuiAccordion-rounded:first-of-type, &.MuiAccordion-rounded:last-of-type':
                        {
                            borderRadius: borderRadius.primary,
                        },
                    [breakpoints.up('lg')]: {
                        '&.introPicBlock': {
                            borderRadius: `${borderRadius.primary} 0 0 ${borderRadius.primary}`,
                            marginRight: '80px',
                        },
                    },
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    ...defaultTheme.MuiPopover.styleOverrides.paper,
                    borderRadius: borderRadius.primary,
                    borderColor: addOpacityToThemeColor(colors.palette.text.secondary, 0.3),
                },
            },
        },
        MuiOutlinedInput: outlinedInputStyles,
        MuiPickersOutlinedInput: outlinedInputStyles,
        MuiChip: defaultTheme.MuiChip,
        MuiAutocomplete: {
            styleOverrides: {
                ...defaultTheme.MuiAutocomplete.styleOverrides,
                root: {
                    ...defaultTheme.MuiAutocomplete.styleOverrides.root,
                    '.MuiOutlinedInput-root .MuiAutocomplete-input': {
                        ...defaultTheme.MuiAutocomplete.styleOverrides.root[
                            '.MuiOutlinedInput-root .MuiAutocomplete-input'
                        ],
                        borderRadius: borderRadius.secondary,
                    },
                },
            },
        },
        MuiSelect: {
            ...defaultTheme.MuiSelect,
            defaultProps: {
                IconComponent: ArrowDropDownIcon,
            },
        },
        MuiAccordion: defaultTheme.MuiAccordion,
        MuiList: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiList.styleOverrides.root,
                    a: {
                        width: '100%',
                        padding: '0 16px',
                    },
                    'a:hover': {
                        textDecoration: 'none',
                    },
                    '&.topNavigationMenu': {
                        border: 'none',
                        backgroundColor: colors.other.background.header,
                        color: colors.palette.text.primary,
                        li: {
                            width: '100%',
                        },
                    },
                    '&.topNavigationMenu li': {
                        borderRadius: borderRadius.topMenu,
                    },
                    '&.topNavigationMenu .selectOpinionTopMenuItem': {
                        display: 'flex',
                        gap: '0.75rem',
                        color: 'white',
                        backgroundColor: 'rgba(29, 81, 86, 1)',
                    },
                },
            },
        },
        MuiListSubheader: defaultTheme.MuiListSubheader,
        MuiMenu: {
            ...defaultTheme.MuiMenu,
            styleOverrides: {
                paper: {
                    boxShadow: '0px 8px 16px -9px rgba(119, 119, 119, 0.32)',
                    borderRadius: '14px',
                    marginTop: '10px',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiMenuItem.styleOverrides.root,
                    borderRadius: '18px',
                    padding: '6px 12px',
                    margin: '4px',
                    '& .MuiTouchRipple-root': {
                        color: addOpacityToThemeColor(colors.palette.primary.main, 0.5),
                    },
                    '& a': {
                        fontWeight: '500',
                        color: colors.palette.text.primary,
                    },
                    '&.active': {
                        '& a': {
                            color: colors.palette.primary['300'],
                            fontWeight: '600',
                            backgroundColor: 'transparent',
                        },
                    },
                    '&.Mui-selected': {
                        color: colors.palette.primary['300'],
                        backgroundColor: 'transparent',
                    },
                    '&:hover': {
                        color: colors.palette.primary['300'],
                        backgroundColor: colors.palette.background.menuItemHover,
                    },
                    '&:not(.active):hover': {
                        backgroundColor: colors.palette.background.menuItemHover,
                        '& a': {
                            backgroundColor: 'white',
                        },
                        '&.accountMenuItem a': {
                            backgroundColor: colors.palette.primary['200'],
                        },
                    },
                    '&.accountMenuItem': {
                        padding: 0,
                        borderRadius: borderRadius.tabs,
                        marginBottom: '6px',
                    },
                    '&.accountMenuItem a': {
                        backgroundColor: 'white',
                        color: colors.palette.primary['500'],
                        border: `1px solid ${colors.palette.border.main}`,
                        borderRadius: borderRadius.tabs,
                        fontWeight: '400',
                    },
                    '&.accountMenuItem:hover a': {
                        backgroundColor: colors.palette.primary['200'],
                        color: 'white',
                        borderColor: 'transparent',
                    },
                    '&.accountMenuItem.active a, &.accountMenuItem:active a': {
                        backgroundColor: colors.palette.primary['400'],
                        color: 'white',
                        borderColor: 'transparent',
                        '&:hover': {
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary['400'],
                                0.9,
                            ),
                        },
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: '10px',
                    '&.radioGroupThemeControl': {
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.palette.border.main}`,
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root': {
                        borderRadius: borderRadius.primary,
                    },
                    '&.radioGroupThemeControl:hover': {
                        borderRadius: borderRadius.primary,
                        borderColor: colors.palette.primary['300'],
                        background: 'transparent',
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root:hover': {
                        background: 'transparent',
                        boxShadow: 'none',
                    },
                    '&.radioGroupThemeControl.radioGroupControlActive:hover .MuiFormControlLabel-root':
                        {
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.01,
                            ),
                        },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled, &.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled span':
                        {
                            cursor: 'default',
                        },
                    '.ProfileQuestionsContent &:has(:not([value=""]):not(:focus):invalid) .MuiInputLabel-root':
                        {
                            color: colors.other.text.error,
                        },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiInputLabel.styleOverrides.root,
                    color: colors.palette.neutral['300'],
                    '&.Mui-focused, &.Mui-error': {
                        color: colors.palette.neutral['300'],
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: borderRadius.secondary,
                    fontFamily: fonts.secondary,
                    '&.Mui-disabled': {
                        backgroundColor: colors.palette.neutral['50'],
                        border: 'none',
                    },
                },
            },
        },
        MuiFormControlLabel: {
            ...defaultTheme.MuiFormControlLabel,
            styleOverrides: {
                ...defaultTheme.MuiFormControlLabel.styleOverrides,
                label: {
                    ...defaultTheme.MuiFormControlLabel.styleOverrides.label,
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['300'],
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiCheckbox.styleOverrides.root,
                    color: colors.palette.neutral['700'],
                    '&:hover': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary['300'], 0.1),
                    },
                    '&:active': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['400'],
                    },
                    '&.Mui-checked': {
                        color: colors.palette.primary['400'],
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiRadio.styleOverrides.root,
                    color: colors.palette.neutral['700'],
                    '&.Mui-checked': {
                        color: colors.palette.primary['400'],
                    },
                    '&:hover': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary['400'], 0.1),
                    },
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['400'],
                    },
                },
            },
        },
        MuiTouchRipple: defaultTheme.MuiTouchRipple,
        MuiFormHelperText: defaultTheme.MuiFormHelperText,
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    success: SuccessIcon(),
                    warning: WarningIcon(),
                    error: ErrorIcon(),
                    info: InfoIcon(),
                },
            },
            styleOverrides: {
                icon: {},
                message: {
                    ...defaultTheme.MuiAlert.styleOverrides.message,
                    color: colors.palette.neutral['700'],
                    fontSize: '0.875rem',
                    fontWeight: '400',
                    '> :first-child': {
                        fontWeight: '600',
                    },
                },
                standard: {
                    border: '0 none',
                },
                outlined: {
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: colors.palette.neutral['700'],
                },
                outlinedInfo: {
                    borderColor: colors.other.text.info,
                },
                outlinedSuccess: {
                    borderColor: colors.other.text.success,
                },
                outlinedError: {
                    borderColor: colors.other.text.error,
                },
                outlinedWarning: {
                    borderColor: colors.other.text.warning,
                },
                standardInfo: {
                    backgroundColor: colors.other.background.info,
                },
                filledInfo: {
                    color: colors.other.text.info,
                    backgroundColor: colors.other.background.info,
                },
                standardSuccess: {
                    backgroundColor: colors.other.background.success,
                },
                filledSuccess: {
                    color: colors.other.text.success,
                    backgroundColor: colors.other.background.success,
                },
                standardError: {
                    backgroundColor: colors.other.background.error,
                },
                filledError: {
                    color: colors.other.text.error,
                    backgroundColor: colors.other.background.error,
                },
                standardWarning: {
                    backgroundColor: colors.other.background.warning,
                },
                filledWarning: {
                    color: colors.other.text.warning,
                    backgroundColor: colors.other.background.warning,
                },
                root: {
                    '&.notificationAlert': {
                        border: '0',
                        position: 'relative',
                    },
                    '&.notificationAlertWarning': {
                        backgroundColor: colors.other.background.warning,
                        '.MuiAlert-message': {
                            color: colors.other.text.warning,
                        },
                    },
                    '&.notificationAlertError': {
                        backgroundColor: colors.other.background.error,
                        '.MuiAlert-message': {
                            color: colors.other.text.error,
                        },
                    },
                    '&.notificationAlertInfo': {
                        backgroundColor: colors.other.background.info,
                        '.MuiAlert-message': {
                            color: colors.other.text.info,
                        },
                    },
                    '&.notificationAlertSuccess': {
                        backgroundColor: colors.other.background.success,
                        '.MuiAlert-message': {
                            color: colors.other.text.success,
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily: fonts.secondary,
                    fontSize: '1rem',
                    fontWeight: '400',
                    lineHeight: '1.65',
                    borderRadius: borderRadius.buttons,
                    textTransform: 'uppercase',
                    boxShadow: 'none',
                    '&:hover': {
                        background: 'transparent',
                        boxShadow: 'none',
                        borderRadius: borderRadius.buttons,
                    },
                    '&:active': {
                        boxShadow: 'none',
                    },
                    '&.language-button': {
                        height: '35px',
                        '&[aria-expanded="true"]': {
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.08,
                            ),
                            border: `1px solid ${addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.16,
                            )}`,
                            padding: '4px 11px',
                        },
                    },
                    '&.surveyCardButton': {
                        color: colors.palette.primary[500],
                        border: `1px solid ${colors.palette.primary[500]}`,
                        backgroundColor: 'transparent',
                    },
                    '&.surveyCardButton:hover': {
                        color: colors.palette.primary[300],
                        borderColor: colors.palette.primary[300],
                        backgroundColor: 'transparent',
                    },
                    '&.surveyCardButton:active': {
                        color: colors.palette.primary[600],
                        borderColor: colors.palette.primary[600],
                        backgroundColor: 'transparent',
                    },
                    '&.surveyLoadMoreButton': {
                        width: '100%',
                        color: colors.palette.primary.main,
                        border: `1px solid ${colors.palette.primary.main}`,
                        background: 'white',
                    },
                    '&.controlYourDataButton': {
                        color: colors.palette.tertiary[500],
                    },
                    '&.controlYourDataButton:hover': {
                        color: colors.palette.tertiary[300],
                    },
                    '&.controlYourDataButton:active': {
                        color: colors.palette.tertiary[600],
                    },
                    [breakpoints.up('md')]: {
                        '&.surveyLoadMoreButton': {
                            width: '40%',
                            maxWidth: 'calc(33.3% - 15px)',
                        },
                    },
                    '&.topMenuButton.selectOpinionTopMenuItem': {
                        display: 'flex',
                        gap: '0.75rem',
                        color: 'white',
                        backgroundColor: 'rgba(29, 81, 86, 1)',
                        ':hover': {
                            color: 'white',
                            backgroundColor: 'rgba(74, 116, 120, 1)',
                        },
                    },
                },
                containedPrimary: {
                    backgroundColor: colors.palette.primary[500],
                    color: 'white',
                    '&:hover': {
                        backgroundColor: colors.palette.primary[300],
                        color: 'white',
                    },
                    '&:active': {
                        backgroundColor: colors.palette.primary[600],
                        color: 'white',
                    },
                    '&:disabled': {
                        backgroundColor: colors.palette.neutral[50],
                        color: colors.palette.neutral[500],
                    },
                },
                containedSecondary: {
                    backgroundColor: colors.palette.tertiary[500],
                    color: 'white',
                    '&:hover': {
                        backgroundColor: colors.palette.tertiary[300],
                        color: 'white',
                    },
                    '&:active': {
                        backgroundColor: colors.palette.tertiary[600],
                        color: 'white',
                    },
                    '&:disabled': {
                        backgroundColor: colors.palette.neutral[50],
                        color: colors.palette.neutral[500],
                    },
                },
                containedSuccess: {
                    '&.copyLinkButton': {
                        backgroundColor: colors.palette.secondary.main,
                        '&:hover': {
                            backgroundColor: colors.palette.secondary.main,
                        },
                        '&:active': {
                            backgroundColor: colors.palette.secondary.main,
                        },
                    },
                },
                outlinedPrimary: {
                    color: colors.palette.primary[500],
                    borderColor: colors.palette.primary[500],
                    '&:hover': {
                        color: colors.palette.primary[300],
                        borderColor: colors.palette.primary[300],
                        backgroundColor: 'transparent',
                    },
                    '&:active': {
                        color: colors.palette.primary[600],
                        borderColor: colors.palette.primary[600],
                        backgroundColor: 'transparent',
                    },
                    '&:disabled': {
                        color: colors.palette.neutral[500],
                        backgroundColor: 'transparent',
                    },
                },
                outlinedSecondary: {
                    color: colors.palette.tertiary[500],
                    borderColor: colors.palette.tertiary[500],
                    '&:hover': {
                        color: colors.palette.tertiary[300],
                        borderColor: colors.palette.tertiary[300],
                        backgroundColor: 'transparent',
                    },
                    '&:active': {
                        color: colors.palette.tertiary[600],
                        borderColor: colors.palette.tertiary[600],
                        backgroundColor: 'transparent',
                    },
                    '&:disabled': {
                        color: colors.palette.neutral[500],
                        backgroundColor: 'transparent',
                    },
                },
                textPrimary: {
                    color: colors.palette.primary[500],
                    '&:hover': {
                        color: colors.palette.primary[300],
                    },
                    '&:active': {
                        color: colors.palette.primary[600],
                    },
                },
                textSecondary: {
                    color: colors.palette.tertiary[500],
                    '&:hover': {
                        color: colors.palette.tertiary[300],
                    },
                    '&:active': {
                        color: colors.palette.tertiary[600],
                    },
                },
                outlined: {
                    '&.loginButton, &.mobileLoginButton, &.logoutButton, &.mobileLogoutButton': {
                        ...headerButtonStyles,
                        border: 'none',
                        margin: 0,
                    },
                    '&.mobileLoginButton, &.mobileLogoutButton': {
                        textIndent: '-9999rem',
                        padding: '8px',
                        minWidth: '0',
                    },
                    '&.mobileLoginButton .topmenuButtonIcon, &.mobileLogoutButton .topmenuButtonIcon':
                        {
                            transform: 'scale(1.55)',
                            marginLeft: '0.5rem',
                        },
                    '&.showMoreButton': {
                        borderColor: colors.palette.secondary.main,
                        color: colors.palette.secondary.main,
                    },
                },
                text: {
                    textTransform: 'none',
                },
            },
            variants: [
                {
                    props: { variant: 'tertiary' },
                    style: headerButtonStyles,
                },
                {
                    props: { variant: 'descriptive' },
                    style: {
                        textTransform: 'none',
                        border: `1px solid ${addOpacityToThemeColor(colors.palette.primary.main, 0.1)}`,
                        borderRadius: borderRadius.primary,
                        '.title': {
                            fontSize: '1rem',
                            fontWeight: '600',
                        },
                        '.description': {
                            fontFamily: fonts.secondary,
                            fontSize: '0.875rem',
                            color: colors.palette.text.primary,
                        },
                        '.chevron': {
                            color: colors.palette.text.secondary,
                        },
                        '&:hover': {
                            color: colors.palette.primary.main,
                            boxShadow: boxShadows.descriptiveShadow,
                            borderRadius: borderRadius.primary,
                            borderColor: addOpacityToThemeColor(colors.palette.primary.main, 0.05),
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.05,
                            ),
                        },
                        '&:hover .chevron': {
                            color: colors.palette.primary.main,
                        },
                    },
                },
            ],
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&.language-info:hover, &.language-info:focus': {
                        backgroundColor: 'transparent',
                    },
                    '&.mobileLocaleButton, &.mobileMenuButton': {
                        color: colors.palette.secondary,
                        '.menuIcon': {
                            width: '20px',
                        },
                    },
                    '&.mobileLocaleButton:hover, &.mobileMenuButton:hover': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary.main, 0.05),
                    },
                    '&.mobileMenuButton, &.mobileCloseMenuButton': {
                        '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                            background: colors.other.text.header,
                        },
                    },
                    '&:hover': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary.main, 0.05),
                    },
                    '&:active': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary.main, 0.5),
                    },
                },
            },
        },
        MuiBackdrop: defaultTheme.MuiBackdrop,
        MuiBadge: {
            styleOverrides: {
                root: {
                    '&.notificationBadge > .MuiBadge-badge': {
                        right: -10,
                    },
                    '&.notificationBadgeWithQty > .MuiBadge-badge': {
                        width: '12px',
                        minWidth: '12px',
                        height: '12px',
                    },
                },
                badge: {
                    fontFamily: fonts.secondary,
                    fontWeight: '400',
                    boxShadow: 'none',
                },
            },
        },
        MuiTooltip: defaultTheme.MuiTooltip,
        MuiStep: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStep.styleOverrides.root,
                    paddingLeft: '0',
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.root,
                    color: colors.palette.neutral['10'],
                    width: '2rem',
                    height: '2rem',
                    backgroundColor: colors.palette.neutral['10'],
                    borderRadius: '50%',
                    '&.Mui-active': {
                        ...defaultTheme.MuiStepIcon.styleOverrides.root['&.Mui-active'],
                        backgroundColor: colors.palette.primary['500'],
                        borderColor: colors.palette.primary['500'],
                        text: {
                            fill: 'white',
                        },
                    },
                    '&.Mui-completed': {
                        backgroundColor: 'white',
                        borderColor: colors.palette.primary['500'],
                    },
                },
                text: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.text,
                    fontWeight: '600',
                    fill: colors.palette.neutral['200'],
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepLabel.styleOverrides.root,
                    '.MuiSvgIcon-root circle': {
                        display: 'none',
                    },
                },
                vertical: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.vertical['.MuiStepLabel-label'],
                        fontFamily: fonts.primary,
                        fontSize: '1rem',
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.vertical[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: '600',
                        },
                    },
                },
                horizontal: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                            '.MuiStepLabel-label'
                        ],
                        fontFamily: fonts.primary,
                        fontSize: '1rem',
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: '600',
                        },
                        [breakpoints.up(420)]: {
                            '&.Mui-active': {
                                display: 'inline-block',
                            },
                        },
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepConnector.styleOverrides.root,
                    paddingRight: '8px',
                    '&.Mui-active': {
                        fill: colors.palette.primary,
                    },
                    '&.Mui-completed': {
                        fill: colors.palette.primary,
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    '&.surveyCard': {
                        boxShadow: 'none',
                        transition: 'border 0.25s',
                        '&:hover': {
                            borderColor: colors.palette.neutral['100'],
                        },
                    },
                    '&.introRegistration': {
                        ...defaultTheme.MuiCard.styleOverrides.root['&.introRegistration'],
                        width: '100%',
                        borderRadius: '0',
                        background: 'transparent',
                        '&:not(:has(.registrationBox))': {
                            '.MuiAlert-root': {
                                backgroundColor: addOpacityToThemeColor(
                                    colors.palette.primary['700'],
                                    0.6,
                                ),
                                border: 'none',
                                '.MuiAlert-message': {
                                    color: 'white',
                                },
                            },
                        },
                    },
                    '&.introRegistration .MuiStack-root': {
                        alignItems: 'stretch',
                    },
                    '.referral &': {
                        padding: variables.padding.cardPadding,
                    },
                    '.referralDetails': {
                        padding: '10px 24px',
                    },
                    '&.referralIntroWrapper': {
                        padding: '16px 24px 24px 24px',
                    },
                    '&.referralCardWrapper': {
                        padding: '0',
                        '.referralDetails': {
                            background: 'white',
                            paddingBottom: '0',
                            padding: '14px 24px',
                        },
                        '.referralsDetailTitle': {
                            fontFamily: fonts.primary,
                            fontSize: '1.125rem',
                            fontWeight: '600',
                        },
                        '.referralDetailContent li': {
                            marginLeft: '1rem',
                            marginTop: '0.25rem',
                        },
                        '.referralDetailSubtitle': {
                            display: 'inline-flex',
                            fontSize: '0.875rem',
                            color: colors.palette.neutral['400'],
                            marginBottom: '0.5rem',
                        },
                    },
                    '&.nmSurveyCard': {
                        '.nmSurveyCardLabel': {
                            color: colors.palette.neutral['400'],
                        },
                        '.nmSurveyCardTitle, .nmSurveyCardBaseInfo': {
                            color: colors.palette.neutral['700'],
                            backgroundColor: colors.palette.neutral['10'],
                        },
                        '.nmSurveyCardInfoTitle': {
                            color: colors.palette.neutral['700'],
                        },
                    },
                },
            },
        },
        MuiDivider: defaultTheme.MuiDivider,
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    gap: '2px',
                },
                root: {
                    ...defaultTheme.MuiTabs.styleOverrides.root,
                    backgroundColor: 'white',
                    minHeight: '40px',
                    borderRadius: borderRadius.tabs,
                    width: '100%',
                    [breakpoints.up('sm')]: {
                        '&.MuiTabs-root': {
                            width: 'fit-content',
                        },
                    },
                    '& .MuiTouchRipple-root': {
                        borderRadius: borderRadius.tabs,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTab.styleOverrides.root,
                    borderRadius: borderRadius.tabs,
                    fontFamily: fonts.primary,
                    padding: '8px 16px',
                    minHeight: '40px',
                    fontSize: '1rem',
                    fontWeight: '400',
                    color: colors.palette.primary['500'],
                    transition: 'background 0.05s ease-in',
                    backgroundColor: 'white',
                    textTransform: 'none',
                    border: 'none',
                    [breakpoints.up('sm')]: {
                        '&.MuiButtonBase-root': {
                            flexBasis: 'auto',
                        },
                    },
                    '&:hover': {
                        ...defaultTheme.MuiTab.styleOverrides.root['&:hover'],
                        color: 'white',
                        backgroundColor: colors.palette.primary['200'],
                    },
                    '&:active': {
                        color: 'white',
                        backgroundColor: colors.palette.primary['400'],
                    },
                    '&.Mui-selected': {
                        ...defaultTheme.MuiTab.styleOverrides.root['&.Mui-selected'],
                        color: 'white',
                        backgroundColor: colors.palette.primary['400'],
                        '&:hover': {
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary['400'],
                                0.9,
                            ),
                        },
                    },
                },
            },
        },
        MuiTabPanel: defaultTheme.MuiTabPanel,
        MuiTableHead: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableHead.styleOverrides.root,
                    fontWeight: '600',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableCell.styleOverrides.root,
                    backgroundColor: 'transparent',
                },
                head: {
                    ...defaultTheme.MuiTableCell.styleOverrides.head,
                    color: colors.palette.neutral['700'],
                    fontFamily: fonts.secondary,
                    fontSize: typography.body2?.fontSize,
                    fontWeight: '600',
                    borderBottomColor: colors.palette.border.main,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableRow.styleOverrides.root,
                    '&:hover': {
                        '& > *:not(th)': {
                            color: colors.palette.primary['200'],
                            '.MuiTypography-root': {
                                color: colors.palette.primary['200'],
                            },
                        },
                    },
                    '&:nth-of-type(odd) td': {
                        backgroundColor: 'white',
                    },
                    '&:nth-of-type(even) td': {
                        backgroundColor: colors.other.table.evenRowHighlight,
                    },
                },
                head: defaultTheme.MuiTableRow.styleOverrides.head,
            },
        },
        MuiPickersPopper: defaultTheme.MuiPickersPopper,
        MuiPickersDay: defaultTheme.MuiPickersDay,
        MuiSkeleton: defaultTheme.MuiSkeleton,
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                        backgroundColor: colors.palette.neutral['200'],
                        opacity: 0.38,
                    },
                    '& .Mui-checked .MuFiSwitch-thumb': {
                        color: colors.palette.primary['400'],
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.palette.primary['400'],
                        opacity: 0.5,
                    },
                    '& .Mui-disabled .MuiSwitch-thumb': {
                        color: colors.palette.neutral['50'],
                    },
                    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: colors.palette.neutral['200'],
                        opacity: 1,
                    },
                },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    borderRadius: '50%',
                    marginBottom: '2px',
                    backgroundColor: 'transparent',
                    color: colors.palette.neutral['700'],
                    '&:not(.MuiPaginationItem-ellipsis):hover': {
                        color: colors.palette.primary['300'],
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary['300'], 0.1),
                    },
                    '&.Mui-selected': {
                        backgroundColor: colors.palette.primary['100'],
                        color: 'white',
                        '&:hover': {
                            backgroundColor: colors.palette.primary['100'],
                        },
                    },
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                        color: colors.palette.neutral['200'],
                    },
                },
                previousNext: {
                    backgroundColor: 'transparent',
                    color: colors.palette.neutral['700'],
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                        color: colors.palette.neutral['200'],
                    },
                },
            },
        },
    },
});

export default theme;
